import * as React from "react";
import { cn } from "@/lib/utils";
import { SpotlightCard, SpotlightCardContent } from "./SpotlightCard";
import { GradientText, SectionLabel } from "./GradientText";

interface Feature {
  id: string;
  title: string;
  description: string;
  illustration: string;
}

interface FeatureSpotlightProps {
  title: string;
  subtitle?: string;
  features: Feature[];
}

export function FeatureSpotlight({
  title,
  subtitle,
  features,
}: FeatureSpotlightProps) {
  const [isVisible, setIsVisible] = React.useState(false);
  const sectionRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 lg:py-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div
          className={cn(
            "text-center max-w-3xl mx-auto mb-16 lg:mb-20",
            "transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]",
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          )}
        >
          <SectionLabel className="mb-6">Spotlight</SectionLabel>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mb-6">
            <GradientText>{title}</GradientText>
          </h2>

          {subtitle && (
            <p className="text-muted-foreground text-base lg:text-lg leading-relaxed">
              {subtitle}
            </p>
          )}
        </div>

        {/* Features grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {features.map((feature, index) => (
            <SpotlightCard
              key={feature.id}
              className={cn(
                "transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]",
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-8"
              )}
              style={{
                transitionDelay: isVisible ? `${index * 80}ms` : "0ms",
              }}
            >
              <SpotlightCardContent className="h-full flex flex-col p-6 lg:p-8">
                {/* Illustration */}
                <div className="aspect-video mb-6 rounded-xl bg-accent/5 border border-accent/10 flex items-center justify-center overflow-hidden">
                  <img
                    src={feature.illustration}
                    alt={feature.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                      e.currentTarget.parentElement!.innerHTML = `
                        <div class="flex items-center justify-center h-full text-accent opacity-50">
                          <svg class="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      `;
                    }}
                  />
                </div>

                {/* Title */}
                <h3 className="text-lg lg:text-xl font-semibold text-foreground mb-3">
                  {feature.title}
                </h3>

                {/* Description */}
                <p className="text-muted-foreground text-sm lg:text-base leading-relaxed flex-1">
                  {feature.description}
                </p>
              </SpotlightCardContent>
            </SpotlightCard>
          ))}
        </div>
      </div>
    </section>
  );
}
